import React, { useState, useRef } from "react";
import Popper from "@material-ui/core/Popper";
import Button from "@material-ui/core/Button";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import KeyboardIcon from "@material-ui/icons/Keyboard";
import { makeStyles } from "@material-ui/core/styles";
import { defaultHotkeys } from "redux/keyboard/defaultHotkeys";
import IntlMessage from "../../helpers/IntlMessage";

const useStyles = makeStyles({
  wrapper: {
    position: "absolute",
    bottom: 60,
    right: 20,
    zIndex: 100,
  },
  button: {
    borderRadius: "50%",
    backgroundColor: "#1871e8",
    height: 32,
    width: 32,
    minWidth: "unset",
    padding: 0,
    color: "white",

    "& .MuiSvgIcon-root": {
      fontSize: "1rem",
    },

    "&:hover": {
      backgroundColor: "#1565c0",
    },
  },
  popper: {
    width: 320,
    border: "1px solid #d1d5db",
    borderRadius: 8,
    zIndex: 100,
    background: "white",
    marginRight: 8,
    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
    overflow: "hidden",
  },
  header: {
    padding: "10px 14px",
    fontWeight: 600,
    fontSize: 13,
    color: "#1871e8",
    borderBottom: "1px solid #e5e7eb",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  row: {
    borderBottom: "1px solid #f0f0f0",
    "&:last-child": {
      borderBottom: "none",
    },
    "&:hover": {
      background: "#f8fafc",
    },
  },
  cellShortcut: {
    padding: "7px 14px",
    fontSize: 12,
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  },
  cellDescription: {
    padding: "7px 14px",
    fontSize: "10px",
    color: "#555",
    verticalAlign: "middle",
  },
  kbd: {
    display: "inline-block",
    padding: "2px 4px",
    fontSize: "8px",
    borderRadius: "4px",
    minWidth: "20px",
    lineHeight: "12px",
    fontFamily: "monospace",
    color: "#333",
    background: "#f3f4f6",
    border: "1px solid #d1d5db",
    boxShadow: "0 1px 0 rgba(0,0,0,0.08)",
    textAlign: "center",
  },
  plus: {
    margin: "0 3px",
    fontSize: 10,
    color: "#9ca3af",
  },
  slash: {
    margin: "0 3px",
    fontSize: 10,
    color: "#9ca3af",
  },
});

const hotkeys = defaultHotkeys;

const HOTKEY_LIST = [
  {
    keys: ["Mouse Wheel"],
    separator: "",
    descriptionId: "keyboard-modal.zoom-in-out",
  },
  {
    keys: [hotkeys.Escape.name],
    separator: "",
    descriptionId: "bottom-hotkeys.cancel-draw-annotation",
  },
  {
    keys: [hotkeys.Control.name, hotkeys.showIrisCircles.name],
    separator: "+",
    descriptionId: "Copy (Bounding Box)",
    secondKeys: [hotkeys.Control.name, hotkeys.v.name],
    secondSeparator: "+",
    secondDescriptionId: "Paste (Bounding Box)",
    combinedSeparator: "/",
  },
  {
    keys: [hotkeys.Control.name, hotkeys.z.name],
    separator: "+",
    descriptionId: "undo-redo-top-bar.undo",
    secondKeys: [hotkeys.Control.name, hotkeys.y.name],
    secondSeparator: "+",
    secondDescriptionId: "undo-redo-top-bar.redo",
    combinedSeparator: "/",
  },
  {
    keys: [hotkeys.Control.name, hotkeys.a.name],
    separator: "+",
    descriptionId: "keyboard-modal.go-to-previous-photo",
  },
  {
    keys: [hotkeys.Control.name, hotkeys.d.name],
    separator: "+",
    descriptionId: "keyboard-modal.go-to-next-photo",
  },
];

export default function HotkeyInfo() {
  const classes = useStyles();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  function handleClickAway() {
    setOpen(false);
  }

  function toggle() {
    setOpen((prev) => !prev);
  }

  function renderKeys(keys, separator) {
    return keys.map((key, idx) => (
      <React.Fragment key={idx}>
        {idx > 0 && <span className={classes.plus}>{separator}</span>}
        <span className={classes.kbd}>{key}</span>
      </React.Fragment>
    ));
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div className={classes.wrapper} id="hotkey-info">
        <Button
          ref={anchorRef}
          onClick={toggle}
          variant="contained"
          color="primary"
          size="small"
          className={classes.button}
        >
          <KeyboardIcon fontSize="small" />
        </Button>
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="left-end"
          className={classes.popper}
        >
          <div className={classes.header}>
            <KeyboardIcon style={{ fontSize: 16 }} />
            <IntlMessage
              id="keyboard-modal.keyboard-shortcuts"
              defaultMessage="Keyboard Shortcuts"
            />
          </div>
          <table className={classes.table}>
            <tbody>
              {HOTKEY_LIST.map((item, idx) => (
                <tr key={idx} className={classes.row}>
                  <td className={classes.cellShortcut}>
                    {renderKeys(item.keys, item.separator)}
                    {item.secondKeys && (
                      <>
                        <span className={classes.slash}>
                          {item.combinedSeparator}
                        </span>
                        {renderKeys(item.secondKeys, item.secondSeparator)}
                      </>
                    )}
                  </td>
                  <td className={classes.cellDescription}>
                    <IntlMessage id={item.descriptionId} />
                    {item.secondDescriptionId && (
                      <>
                        {" / "}
                        <IntlMessage id={item.secondDescriptionId} />
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Popper>
      </div>
    </ClickAwayListener>
  );
}
